import React, { useState, useEffect, useCallback, useRef } from "react";
import { Modal, Input, List, Avatar, Typography, Space, Tag, Empty, Spin, Card, Row, Col, Tabs, message } from "antd";
import { SearchOutlined, UserOutlined, HomeOutlined, ClockCircleOutlined, StarOutlined, StarFilled } from "@ant-design/icons";
import { useList } from "@refinedev/core";

const { Search } = Input;
const { Text } = Typography;
const { TabPane } = Tabs;

// Eigene debounce Implementierung
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

interface ModernSearchModalProps<T> {
  visible: boolean;
  onClose: () => void;
  onSelect: (item: T) => void;
  resource: string;
  title: string;
  searchFields: string[];
  renderItem: (item: T) => {
    title: string;
    description?: string;
    meta?: string;
    avatar?: React.ReactNode;
  };
  placeholder?: string;
}

export const ModernSearchModal = <T extends Record<string, any>>({
  visible,
  onClose,
  onSelect,
  resource,
  title,
  searchFields,
  renderItem,
  placeholder = "Suchen...",
}: ModernSearchModalProps<T>) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("search");
  const [recentItems, setRecentItems] = useState<T[]>([]);
  const [favoriteItems, setFavoriteItems] = useState<T[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchInputRef = useRef<any>(null);

  // Server-seitige Suche mit Pagination
  const { data, isLoading, refetch } = useList<T>({
    resource,
    pagination: {
      pageSize: 20,
      current: 1,
    },
    filters: searchTerm ? searchFields.map(field => ({
      field,
      operator: "contains" as const,
      value: searchTerm,
    })) : [],
    queryOptions: {
      enabled: searchTerm.length > 2 || searchTerm.length === 0,
      staleTime: 30 * 1000, // 30 Sekunden Cache
      onSuccess: (data) => {
        console.log(`🔍 ModernSearchModal ${resource} - Search successful:`, data);
      },
      onError: (error) => {
        console.error(`❌ ModernSearchModal ${resource} - Search error:`, error);
      },
    },
  });

  // Debug logging
  React.useEffect(() => {
    console.log(`🔍 ModernSearchModal ${resource} - State:`, {
      searchTerm,
      isLoading,
      dataCount: data?.data?.length || 0,
      visible
    });
  }, [resource, searchTerm, isLoading, data, visible]);

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setSearchTerm(value);
      refetch();
    }, 300),
    []
  );

  // Lade Recent & Favorites aus LocalStorage
  useEffect(() => {
    if (visible) {
      const recentKey = `recent_${resource}`;
      const favoriteKey = `favorites_${resource}`;
      
      const recent = JSON.parse(localStorage.getItem(recentKey) || "[]");
      const favorites = JSON.parse(localStorage.getItem(favoriteKey) || "[]");
      
      setRecentItems(recent.slice(0, 5));
      setFavoriteItems(favorites);
      
      // Fokus auf Suchfeld
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [visible, resource]);

  // Keyboard Navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!visible) return;

      const items = data?.data || [];
      
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) => Math.min(prev + 1, items.length - 1));
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case "Enter":
          e.preventDefault();
          if (items[selectedIndex]) {
            handleSelect(items[selectedIndex]);
          }
          break;
        case "Escape":
          e.preventDefault();
          onClose();
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [visible, data, selectedIndex]);

  const handleSelect = (item: T) => {
    // Speichere in Recent
    const recentKey = `recent_${resource}`;
    const recent = JSON.parse(localStorage.getItem(recentKey) || "[]");
    const filtered = recent.filter((r: any) => r.id !== item.id);
    const updated = [item, ...filtered].slice(0, 10);
    localStorage.setItem(recentKey, JSON.stringify(updated));

    onSelect(item);
    onClose();
    
    // Reset
    setSearchTerm("");
    setSelectedIndex(0);
  };

  const toggleFavorite = (item: T, e: React.MouseEvent) => {
    e.stopPropagation();
    const favoriteKey = `favorites_${resource}`;
    const favorites = JSON.parse(localStorage.getItem(favoriteKey) || "[]");
    
    const isFavorite = favorites.some((f: any) => f.id === item.id);
    
    if (isFavorite) {
      const filtered = favorites.filter((f: any) => f.id !== item.id);
      localStorage.setItem(favoriteKey, JSON.stringify(filtered));
      setFavoriteItems(filtered);
      message.success("Aus Favoriten entfernt");
    } else {
      const updated = [...favorites, item];
      localStorage.setItem(favoriteKey, JSON.stringify(updated));
      setFavoriteItems(updated);
      message.success("Zu Favoriten hinzugefügt");
    }
  };

  const isFavorite = (item: T) => {
    return favoriteItems.some((f) => f.id === item.id);
  };

  const renderListItem = (item: T, index: number) => {
    const { title, description, meta, avatar } = renderItem(item);
    const isSelected = index === selectedIndex && activeTab === "search";

    return (
      <List.Item
        key={item.id}
        onClick={() => handleSelect(item)}
        style={{
          cursor: "pointer",
          backgroundColor: isSelected ? "#f0f2f5" : "transparent",
          padding: "12px 16px",
          borderRadius: "8px",
          marginBottom: "4px",
        }}
        onMouseEnter={() => setSelectedIndex(index)}
      >
        <List.Item.Meta
          avatar={avatar || <Avatar icon={<UserOutlined />} />}
          title={
            <Space>
              <Text strong>{title}</Text>
              {meta && <Text type="secondary" style={{ fontSize: "12px" }}>{meta}</Text>}
            </Space>
          }
          description={description}
        />
        <div
          onClick={(e) => toggleFavorite(item, e)}
          style={{ cursor: "pointer" }}
        >
          {isFavorite(item) ? (
            <StarFilled style={{ color: "#faad14", fontSize: "18px" }} />
          ) : (
            <StarOutlined style={{ fontSize: "18px" }} />
          )}
        </div>
      </List.Item>
    );
  };

  return (
    <Modal
      title={
        <Space>
          <SearchOutlined />
          <span>{title}</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={800}
      footer={null}
      bodyStyle={{ padding: 0 }}
    >
      <div style={{ padding: "16px 24px", borderBottom: "1px solid #f0f0f0" }}>
        <Search
          ref={searchInputRef}
          placeholder={placeholder}
          allowClear
          size="large"
          onSearch={(value) => debouncedSearch(value)}
          onChange={(e) => debouncedSearch(e.target.value)}
          style={{ width: "100%" }}
        />
      </div>

      <Tabs 
        activeKey={activeTab} 
        onChange={setActiveTab}
        style={{ padding: "0 24px" }}
      >
        <TabPane 
          tab={
            <Space>
              <SearchOutlined />
              Suchergebnisse
            </Space>
          } 
          key="search"
        >
          <div style={{ height: "400px", overflowY: "auto", padding: "8px 0" }}>
            {isLoading ? (
              <div style={{ textAlign: "center", padding: "50px" }}>
                <Spin size="large" />
                <div style={{ marginTop: "16px" }}>Suche läuft...</div>
              </div>
            ) : data?.data && data.data.length > 0 ? (
              <List
                dataSource={data.data}
                renderItem={renderListItem}
              />
            ) : searchTerm.length > 2 ? (
              <Empty 
                description={`Keine Ergebnisse für "${searchTerm}"`}
                style={{ marginTop: "50px" }}
              />
            ) : (
              <Empty 
                description="Geben Sie mindestens 3 Zeichen ein"
                style={{ marginTop: "50px" }}
              />
            )}
          </div>
        </TabPane>

        <TabPane 
          tab={
            <Space>
              <ClockCircleOutlined />
              Zuletzt verwendet
            </Space>
          } 
          key="recent"
        >
          <div style={{ height: "400px", overflowY: "auto", padding: "8px 0" }}>
            {recentItems.length > 0 ? (
              <List
                dataSource={recentItems}
                renderItem={(item, index) => renderListItem(item, index)}
              />
            ) : (
              <Empty 
                description="Noch keine zuletzt verwendeten Einträge"
                style={{ marginTop: "50px" }}
              />
            )}
          </div>
        </TabPane>

        <TabPane 
          tab={
            <Space>
              <StarOutlined />
              Favoriten
            </Space>
          } 
          key="favorites"
        >
          <div style={{ height: "400px", overflowY: "auto", padding: "8px 0" }}>
            {favoriteItems.length > 0 ? (
              <List
                dataSource={favoriteItems}
                renderItem={(item, index) => renderListItem(item, index)}
              />
            ) : (
              <Empty 
                description="Noch keine Favoriten markiert"
                style={{ marginTop: "50px" }}
              />
            )}
          </div>
        </TabPane>
      </Tabs>

      <div style={{ 
        padding: "12px 24px", 
        borderTop: "1px solid #f0f0f0",
        color: "#8c8c8c",
        fontSize: "12px"
      }}>
        <Space split="|">
          <span>↑↓ Navigation</span>
          <span>Enter Auswählen</span>
          <span>ESC Schließen</span>
          <span>⭐ Favorit</span>
        </Space>
      </div>
    </Modal>
  );
}; 